import React from "react";
import { ExternalLink, Crown, Swords } from "lucide-react";
import { cn } from "../lib/utils";

interface IskambilBacklinkBannerProps {
  variant?: "footer" | "minimal";
  className?: string;
}

export const IskambilBacklinkBanner: React.FC<IskambilBacklinkBannerProps> = ({
  variant = "footer",
  className,
}) => {
  const isMinimal = variant === "minimal";

  if (isMinimal) {
    return (
      <div className={cn("w-full my-3", className)}>
        {/* Header Title */}
        <div className="flex items-center justify-between gap-1 mb-1.5 px-1">
          <span className="text-[9px] font-black tracking-widest text-cyan-400 uppercase drop-shadow-[0_0_8px_rgba(34,211,238,0.5)]">
            DİĞER OYUNLARIMIZ
          </span>
          <span className="text-[10px] text-cyan-400/70 font-semibold tracking-wider">
            ♠ ♥ ♣ ♦
          </span>
        </div>

        {/* Minimal Card Container */}
        <a
          href="https://iskambil.online"
          target="_blank"
          rel="noopener noreferrer"
          title="iskambil.online - yapay zekaya karşı ücretsiz batak, king, pişti ve iskambil oyunları"
          className="group relative block w-full p-3.5 rounded-2xl bg-gradient-to-r from-[#020b18] via-[#041d36] to-[#020d1c] border-2 border-cyan-400/70 shadow-[0_0_20px_rgba(6,182,212,0.25),inset_0_0_10px_rgba(6,182,212,0.1)] hover:border-cyan-300 hover:shadow-[0_0_30px_rgba(6,182,212,0.45)] transition-all duration-200 overflow-hidden cursor-pointer"
        >
          {/* Ambient Glow */}
          <div className="absolute -top-10 -right-10 w-28 h-28 bg-cyan-500/20 rounded-full blur-2xl pointer-events-none group-hover:bg-cyan-400/30 transition-colors" />

          {/* Main Info Row */}
          <div className="flex items-center justify-between gap-2 mb-2.5 relative z-10">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-600/30 border border-cyan-400/40 flex items-center justify-center text-base shrink-0 shadow-inner group-hover:scale-105 transition-transform">
                🃏
              </div>
              <div className="min-w-0">
                <span className="text-base font-black tracking-tight text-white block leading-tight">
                  iskambil<span className="text-cyan-400 font-extrabold">.online</span>
                </span>
                <span className="text-[10px] text-slate-300 font-bold block truncate">
                  Pişti • Batak • King • 66 • 51
                </span>
              </div>
            </div>

            <div className="w-8 h-8 rounded-xl border-2 border-cyan-400/80 bg-cyan-500/15 group-hover:bg-cyan-400 group-hover:text-slate-950 text-cyan-300 flex items-center justify-center shrink-0 transition-all shadow-[0_0_12px_rgba(6,182,212,0.3)]">
              <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </div>
          </div>

          {/* Minimal Badges Row */}
          <div className="grid grid-cols-5 gap-1 pt-2 border-t border-cyan-500/20 relative z-10">
            <div className="bg-gradient-to-b from-blue-600/60 to-slate-900 border border-cyan-400/40 rounded-lg py-1 px-0.5 flex flex-col items-center justify-center text-[9px] font-black text-white text-center leading-none">
              <span className="text-[10px] mb-0.5">🃏</span>
              <span>Pişti</span>
            </div>
            <div className="bg-gradient-to-b from-slate-800 to-slate-950 border border-amber-400/40 rounded-lg py-1 px-0.5 flex flex-col items-center justify-center text-[9px] font-black text-amber-300 text-center leading-none">
              <span className="text-[10px] mb-0.5 text-amber-400">♣</span>
              <span>Batak</span>
            </div>
            <div className="bg-gradient-to-b from-cyan-900/80 to-slate-950 border border-cyan-400/50 rounded-lg py-1 px-0.5 flex flex-col items-center justify-center text-[9px] font-black text-cyan-200 text-center leading-none">
              <span className="text-[10px] mb-0.5 text-amber-300">👑</span>
              <span>King</span>
            </div>
            <div className="bg-gradient-to-b from-red-900/80 to-slate-950 border border-amber-500/50 rounded-lg py-1 px-0.5 flex flex-col items-center justify-center text-[9px] font-black text-amber-300 text-center leading-none">
              <span className="text-[10px] font-mono font-bold mb-0.5 text-amber-400">66</span>
              <span>66</span>
            </div>
            <div className="bg-gradient-to-b from-indigo-900/80 to-slate-950 border border-cyan-400/40 rounded-lg py-1 px-0.5 flex flex-col items-center justify-center text-[9px] font-black text-cyan-300 text-center leading-none">
              <span className="text-[10px] mb-0.5">🎴</span>
              <span>Solitaire</span>
            </div>
          </div>
        </a>
      </div>
    );
  }

  // Footer Variant (Large Full Feature Banner matching screenshot file_000000004b5881f4b47ceb8922b26809.png)
  return (
    <div className={cn("w-full max-w-6xl mx-auto my-8 px-2 relative z-20", className)}>
      {/* Header Title Above Container */}
      <div className="flex items-center justify-center gap-2 mb-3">
        <h3 className="text-sm sm:text-base font-black tracking-[0.25em] text-cyan-400 uppercase drop-shadow-[0_0_15px_rgba(34,211,238,0.6)]">
          DİĞER OYUNLARIMIZ
        </h3>
        <span className="text-cyan-400/80 text-xs sm:text-sm tracking-widest font-semibold">
          ♠ ♥ ♣ ♦
        </span>
      </div>

      {/* Main Banner Card */}
      <a
        href="https://iskambil.online"
        target="_blank"
        rel="noopener noreferrer"
        title="iskambil.online - yapay zekaya karşı ücretsiz batak, king, pişti ve iskambil oyunları"
        className="group relative block w-full p-4 sm:p-6 md:p-7 rounded-[28px] bg-gradient-to-r from-[#020d1c] via-[#041d36] to-[#020b18] border-2 border-cyan-400/80 shadow-[0_0_35px_rgba(6,182,212,0.35),inset_0_0_20px_rgba(6,182,212,0.12)] hover:border-cyan-300 hover:shadow-[0_0_55px_rgba(6,182,212,0.55),inset_0_0_25px_rgba(6,182,212,0.2)] transition-all duration-300 overflow-hidden cursor-pointer"
      >
        {/* Glowing Background Radial Highlights */}
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-80 h-80 bg-cyan-500/15 rounded-full blur-3xl pointer-events-none group-hover:bg-cyan-400/25 transition-colors" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

        {/* Content Layout */}
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          {/* Left Column: 3D Cards Visual & Game App Badges */}
          <div className="lg:col-span-7 flex flex-col gap-4">
            {/* 3D Visual Card Fan Composite */}
            <div className="relative h-28 sm:h-32 w-full flex items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-b from-cyan-950/40 to-slate-950/60 border border-cyan-500/20 p-2">
              {/* Golden Crown on Left */}
              <div className="absolute left-3 sm:left-6 bottom-3 z-20 text-amber-400 drop-shadow-[0_0_12px_rgba(251,191,36,0.8)] animate-pulse">
                <Crown className="w-8 h-8 sm:w-10 sm:h-10 fill-amber-400/20" />
              </div>

              {/* Fanned Playing Cards Composite */}
              <div className="relative flex items-center justify-center h-full w-64 sm:w-80">
                {/* Joker Card */}
                <div className="absolute -left-6 sm:-left-8 top-2 w-14 sm:w-16 h-20 sm:h-24 bg-gradient-to-br from-amber-100 to-slate-200 rounded-lg border-2 border-slate-900 shadow-xl transform -rotate-[22deg] group-hover:-rotate-[26deg] transition-transform duration-300 flex flex-col items-center justify-between p-1 z-10">
                  <span className="text-[10px] font-black text-purple-700 self-start leading-none">
                    J
                  </span>
                  <span className="text-xl sm:text-2xl">🤡</span>
                  <span className="text-[10px] font-black text-purple-700 self-end leading-none rotate-180">
                    J
                  </span>
                </div>

                {/* Ace of Spades */}
                <div className="absolute left-8 sm:left-12 top-1 w-14 sm:w-16 h-20 sm:h-24 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg border-2 border-slate-900 shadow-xl transform -rotate-[8deg] group-hover:-rotate-[11deg] transition-transform duration-300 flex flex-col items-center justify-between p-1 z-20">
                  <span className="text-[10px] font-black text-slate-900 self-start leading-none">
                    A♠
                  </span>
                  <span className="text-xl sm:text-2xl text-slate-950">♠</span>
                  <span className="text-[10px] font-black text-slate-900 self-end leading-none rotate-180">
                    A♠
                  </span>
                </div>

                {/* King of Hearts */}
                <div className="absolute right-12 sm:right-16 top-0 w-14 sm:w-16 h-20 sm:h-24 bg-gradient-to-br from-rose-50 to-slate-100 rounded-lg border-2 border-slate-900 shadow-xl transform rotate-[6deg] group-hover:rotate-[9deg] transition-transform duration-300 flex flex-col items-center justify-between p-1 z-30">
                  <span className="text-[10px] font-black text-red-600 self-start leading-none">
                    K♥
                  </span>
                  <span className="text-xl sm:text-2xl">👑</span>
                  <span className="text-[10px] font-black text-red-600 self-end leading-none rotate-180">
                    K♥
                  </span>
                </div>

                {/* Queen of Hearts */}
                <div className="absolute -right-4 sm:-right-6 top-2 w-14 sm:w-16 h-20 sm:h-24 bg-gradient-to-br from-rose-50 to-slate-100 rounded-lg border-2 border-slate-900 shadow-xl transform rotate-[20deg] group-hover:rotate-[24deg] transition-transform duration-300 flex flex-col items-center justify-between p-1 z-20">
                  <span className="text-[10px] font-black text-red-600 self-start leading-none">
                    Q♥
                  </span>
                  <span className="text-xl sm:text-2xl">👸</span>
                  <span className="text-[10px] font-black text-red-600 self-end leading-none rotate-180">
                    Q♥
                  </span>
                </div>
              </div>

              {/* Glowing Poker Chips / Aces Symbol on Right */}
              <div className="absolute right-3 sm:right-6 bottom-2 z-20 text-cyan-400 drop-shadow-[0_0_12px_rgba(34,211,238,0.8)]">
                <span className="text-2xl sm:text-3xl font-black">♠</span>
              </div>
            </div>

            {/* 9 Game App Badges / Buttons Bar */}
            <div className="grid grid-cols-9 gap-1.5 sm:gap-2">
              {/* 1. Pişti */}
              <div className="bg-gradient-to-b from-blue-600/90 via-blue-700/90 to-slate-950 border border-cyan-400/50 rounded-xl py-1.5 px-1 flex flex-col items-center justify-center text-center shadow-md hover:scale-105 transition-transform cursor-pointer">
                <span className="text-xs sm:text-sm mb-0.5">🃏</span>
                <span className="text-[9px] sm:text-[10px] font-black text-white tracking-tight leading-none">
                  Pişti
                </span>
              </div>

              {/* 2. Batak */}
              <div className="bg-gradient-to-b from-slate-800 via-slate-900 to-slate-950 border border-amber-400/50 rounded-xl py-1.5 px-1 flex flex-col items-center justify-center text-center shadow-md hover:scale-105 transition-transform cursor-pointer">
                <span className="text-xs sm:text-sm mb-0.5 text-amber-400">
                  ♣
                </span>
                <span className="text-[9px] sm:text-[10px] font-black text-amber-200 tracking-tight leading-none">
                  Batak
                </span>
              </div>

              {/* 3. King */}
              <div className="bg-gradient-to-b from-cyan-900/90 via-slate-900 to-slate-950 border border-cyan-400/60 rounded-xl py-1.5 px-1 flex flex-col items-center justify-center text-center shadow-md hover:scale-105 transition-transform cursor-pointer">
                <span className="text-xs sm:text-sm mb-0.5 text-amber-300">
                  👑
                </span>
                <span className="text-[9px] sm:text-[10px] font-black text-cyan-200 tracking-tight leading-none">
                  King
                </span>
              </div>

              {/* 4. 66 */}
              <div className="bg-gradient-to-b from-red-950 via-red-900/80 to-slate-950 border border-amber-500/60 rounded-xl py-1.5 px-1 flex flex-col items-center justify-center text-center shadow-md hover:scale-105 transition-transform cursor-pointer">
                <span className="text-xs sm:text-sm font-black font-mono text-amber-400 mb-0.5 leading-none">
                  66
                </span>
                <span className="text-[9px] sm:text-[10px] font-black text-amber-200 tracking-tight leading-none">
                  66
                </span>
              </div>

              {/* 5. 51 */}
              <div className="bg-gradient-to-b from-blue-950 via-slate-900 to-slate-950 border border-cyan-400/50 rounded-xl py-1.5 px-1 flex flex-col items-center justify-center text-center shadow-md hover:scale-105 transition-transform cursor-pointer">
                <span className="text-xs sm:text-sm font-black font-mono text-cyan-300 mb-0.5 leading-none">
                  51
                </span>
                <span className="text-[9px] sm:text-[10px] font-black text-cyan-200 tracking-tight leading-none">
                  51
                </span>
              </div>

              {/* 6. Maça Kızı */}
              <div className="bg-gradient-to-b from-rose-950 via-slate-900 to-slate-950 border border-rose-400/50 rounded-xl py-1.5 px-1 flex flex-col items-center justify-center text-center shadow-md hover:scale-105 transition-transform cursor-pointer">
                <span className="text-xs sm:text-sm mb-0.5">👸</span>
                <span className="text-[8px] sm:text-[9px] font-black text-rose-200 tracking-tight leading-none truncate w-full">
                  Maça Kızı
                </span>
              </div>

              {/* 7. Savaş */}
              <div className="bg-gradient-to-b from-indigo-950 via-slate-900 to-slate-950 border border-indigo-400/50 rounded-xl py-1.5 px-1 flex flex-col items-center justify-center text-center shadow-md hover:scale-105 transition-transform cursor-pointer">
                <span className="text-xs sm:text-sm mb-0.5 text-indigo-300">
                  ⚔️
                </span>
                <span className="text-[9px] sm:text-[10px] font-black text-indigo-200 tracking-tight leading-none">
                  Savaş
                </span>
              </div>

              {/* 8. Papaz Kaçtı */}
              <div className="bg-gradient-to-b from-purple-950 via-slate-900 to-slate-950 border border-purple-400/50 rounded-xl py-1.5 px-1 flex flex-col items-center justify-center text-center shadow-md hover:scale-105 transition-transform cursor-pointer">
                <span className="text-xs sm:text-sm mb-0.5">🎭</span>
                <span className="text-[8px] sm:text-[9px] font-black text-purple-200 tracking-tight leading-none truncate w-full">
                  Papaz Kaçtı
                </span>
              </div>

              {/* 9. Solitaire */}
              <div className="bg-gradient-to-b from-cyan-950 via-slate-900 to-slate-950 border border-cyan-400/50 rounded-xl py-1.5 px-1 flex flex-col items-center justify-center text-center shadow-md hover:scale-105 transition-transform cursor-pointer">
                <span className="text-xs sm:text-sm mb-0.5">🎴</span>
                <span className="text-[8px] sm:text-[9px] font-black text-cyan-200 tracking-tight leading-none truncate w-full">
                  Solitaire
                </span>
              </div>
            </div>
          </div>

          {/* Vertical Separator for Desktop */}
          <div className="hidden lg:block lg:col-span-1 h-32 w-px bg-gradient-to-b from-transparent via-cyan-400/40 to-transparent justify-self-center" />

          {/* Right Column: Brand Name, Button & Games List */}
          <div className="lg:col-span-4 flex flex-col justify-center gap-3">
            {/* Brand Title & External Link Button */}
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <span className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight block drop-shadow-md">
                  iskambil<span className="text-[#00d2ff] font-extrabold">.online</span>
                </span>
              </div>

              {/* External Link Square Box */}
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl border-2 border-cyan-400/80 bg-cyan-500/15 group-hover:bg-cyan-400 group-hover:text-slate-950 text-cyan-300 flex items-center justify-center shrink-0 transition-all duration-300 shadow-[0_0_20px_rgba(6,182,212,0.4)] group-hover:scale-110">
                <ExternalLink className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </div>
            </div>

            {/* Game List Text Lines */}
            <div className="space-y-1 text-slate-200 text-xs sm:text-sm font-semibold tracking-wide leading-relaxed border-t border-cyan-500/20 pt-2.5">
              <p className="flex items-center gap-1.5 flex-wrap">
                <span>Pişti</span>
                <span className="text-cyan-400/60">•</span>
                <span>Batak</span>
                <span className="text-cyan-400/60">•</span>
                <span>King</span>
                <span className="text-cyan-400/60">•</span>
                <span>66</span>
                <span className="text-cyan-400/60">•</span>
                <span>51</span>
              </p>
              <p className="flex items-center gap-1.5 flex-wrap text-slate-300">
                <span>Maça Kızı</span>
                <span className="text-cyan-400/60">•</span>
                <span>Savaş</span>
                <span className="text-cyan-400/60">•</span>
                <span>Papaz Kaçtı</span>
                <span className="text-cyan-400/60">•</span>
                <span>Solitaire</span>
              </p>
            </div>
          </div>
        </div>
      </a>
    </div>
  );
};
